import React, { useEffect, useRef, useState } from "react";
import {
  ACTIONS,
  isNoteRequiredForAction,
  NOTE_MAX_LENGTH,
  NOTE_MIN_LENGTH,
  STATUS_LABEL,
} from "../../utils/checklistStatus";
import "../../assets/styles/status-change-notes-modal.scss";

// Human-readable, present-tense verb for the modal heading — e.g.
// "Return — In Review → Open" (TRD §3.9, AC-F1.6). Kept local to the
// modal rather than added to checklistStatus.js, which already exports the
// past-tense ACTION_LABEL for the notes history (§3.8) and should not carry
// a second, presentation-only vocabulary.
const ACTION_TITLE = {
  [ACTIONS.COMPLETE]: "Complete",
  [ACTIONS.APPROVE]: "Approve",
  [ACTIONS.RETURN]: "Return",
  [ACTIONS.RECALL]: "Recall",
};

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const HEADING_ID = "statusChangeNotesModalLabel";
const ERROR_ID = "statusChangeNotesModalError";
const HELP_ID = "statusChangeNotesModalHelp";
const TEXTAREA_ID = "statusChangeNotesModalTextarea";

/**
 * The single dialog shown before every checklist status transition
 * (Complete, Approve, Return, Recall). Shared and action-keyed: the note
 * requirement is driven entirely by `isNoteRequiredForAction(action)` from
 * `utils/checklistStatus.js`, never by the status pair, because Recall and
 * Return-from-In-Review share the same {2,3} -> 1 transition (TRD D9).
 *
 * Self-contained and prop-driven — it has no consumers yet (Phase 3 wires
 * the board and detail screens to it). It never issues the API call itself;
 * it trims the note and hands it to `onConfirm`, and the caller owns the
 * request and the `busy`/`serverError` state (TRD §2.2.2, §3.9).
 *
 * Props:
 * - action:      one of ACTIONS.{COMPLETE,APPROVE,RETURN,RECALL}
 * - fromStatus:  current STATUS value the checklist is transitioning from
 * - toStatus:    STATUS value the checklist is transitioning to
 * - isOpen:      whether the dialog is shown
 * - onConfirm:   (note: string | null) => void — trimmed note, or null when empty
 * - onCancel:    () => void — Cancel, Escape, close button, or backdrop-safe no-op
 * - serverError: string | null — rendered inline, never as a toast
 * - busy:        true while the caller's request is in flight
 */
const StatusChangeNotesModal = ({
  action,
  fromStatus,
  toStatus,
  isOpen,
  onConfirm,
  onCancel,
  serverError,
  busy,
}) => {
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);
  const textareaRef = useRef(null);
  const dialogRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);
  // Belt-and-braces guard against a double-click issuing two requests before
  // the caller's `busy` prop has had a chance to propagate back down
  // (AC-F1.9). Reset once the caller reports the in-flight request is done,
  // or when a fresh action opens the modal again.
  const submittingRef = useRef(false);

  const required = isNoteRequiredForAction(action);
  const trimmedLength = note.trim().length;
  const isInvalid = required && trimmedLength < NOTE_MIN_LENGTH;
  const remaining = NOTE_MAX_LENGTH - note.length;

  // Fresh state and focus-to-textarea on every open; focus restored to the
  // trigger on close (TRD §3.9, PRD §5.3, AC-T14).
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElementRef.current = document.activeElement;
      setNote("");
      setTouched(false);
      submittingRef.current = false;
      const focusTimer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
      return () => clearTimeout(focusTimer);
    }

    const previouslyFocused = previouslyFocusedElementRef.current;
    if (previouslyFocused instanceof HTMLElement) {
      previouslyFocused.focus();
    }
    previouslyFocusedElementRef.current = null;
    return undefined;
  }, [isOpen]);

  // Release the in-flight guard once the caller's request settles.
  useEffect(() => {
    if (!busy) {
      submittingRef.current = false;
    }
  }, [busy]);

  const handleCancel = () => {
    if (busy || submittingRef.current) return;
    onCancel();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      handleCancel();
      return;
    }

    if (event.key === "Tab") {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll(FOCUSABLE_SELECTOR)
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const handleChange = (event) => {
    setNote(event.target.value.slice(0, NOTE_MAX_LENGTH));
  };

  const handleConfirm = () => {
    if (busy || submittingRef.current) return;
    if (isInvalid) {
      setTouched(true);
      return;
    }
    submittingRef.current = true;
    const trimmed = note.trim();
    onConfirm(trimmed.length > 0 ? trimmed : null);
  };

  if (!isOpen) return null;

  const actionTitle = ACTION_TITLE[action] || "Update";
  const fromLabel = STATUS_LABEL[fromStatus] ?? "";
  const toLabel = STATUS_LABEL[toStatus] ?? "";
  const showTransition = fromLabel !== "" && toLabel !== "";
  const confirmDisabled = isInvalid || Boolean(busy);

  return (
    <>
      <div className="modal-backdrop show status-change-notes-modal-backdrop" />
      <div
        className="modal fade show d-block status-change-notes-modal"
        id="statusChangeNotesModal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby={HEADING_ID}
        aria-describedby={serverError ? ERROR_ID : undefined}
        data-bs-backdrop="static"
        onKeyDown={handleKeyDown}
      >
        <div className="modal-dialog modal-dialog-centered" ref={dialogRef}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id={HEADING_ID}>
                {actionTitle}
                {showTransition ? ` — ${fromLabel} → ${toLabel}` : null}
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                disabled={busy}
                onClick={handleCancel}
              />
            </div>
            <div className="modal-body">
              {serverError ? (
                <div className="alert alert-danger" role="alert" id={ERROR_ID}>
                  {serverError}
                </div>
              ) : null}

              <label
                htmlFor={TEXTAREA_ID}
                className="status-change-notes-label"
              >
                Note
                {required ? (
                  <>
                    {" "}
                    <span className="status-change-notes-required-marker" aria-hidden="true">
                      *
                    </span>
                    <span className="visually-hidden"> required</span>
                  </>
                ) : null}
              </label>
              <textarea
                id={TEXTAREA_ID}
                ref={textareaRef}
                className="form-control status-change-notes-textarea"
                rows={4}
                maxLength={NOTE_MAX_LENGTH}
                value={note}
                aria-required={required}
                aria-describedby={HELP_ID}
                aria-invalid={touched && isInvalid}
                onChange={handleChange}
                onBlur={() => setTouched(true)}
              />
              <div className="status-change-notes-meta" id={HELP_ID}>
                <span
                  className="status-change-notes-remaining"
                  aria-live="polite"
                >
                  {remaining} characters remaining
                </span>
                {required ? (
                  <span className="status-change-notes-required-hint">
                    A reason is required — at least {NOTE_MIN_LENGTH} characters.
                  </span>
                ) : null}
              </div>
              {touched && isInvalid ? (
                <div className="status-change-notes-validation" role="alert">
                  Enter at least {NOTE_MIN_LENGTH} characters to continue.
                </div>
              ) : null}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn status-change-notes-confirm"
                style={{
                  background: confirmDisabled ? "grey" : undefined,
                  cursor: confirmDisabled ? "not-allowed" : "pointer",
                }}
                disabled={confirmDisabled}
                onClick={handleConfirm}
              >
                {busy ? "Submitting…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StatusChangeNotesModal;
